/* eslint-disable react/iframe-missing-sandbox */
import React, { useCallback, useLayoutEffect } from 'react';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { Modal, TransparentModal, useResponsive } from '@sg/garnish';

export const PersonalDataDetailsScreen = () => {
  const { match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  const navigation = useNavigation();
  const router = useRouter();
  const routeParams = useLocalSearchParams<{ action?: string }>();

  // Personal data request can be for access or deletion.
  const isAccessRequest = routeParams.action === 'access';

  // Dismissing modal just goes back on navigation.
  const handleDismiss = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.dismissTo('/account/info', { withAnchor: true });
  }, [router]);

  // Dynamic title based on received navigation parameter.
  const headerTitle = isAccessRequest
    ? formatMessage(messages.accessCta)
    : formatMessage(messages.deleteCta);

  // Mobile title.
  const setOptions = navigation.setOptions;

  useLayoutEffect(() => {
    setOptions({ headerTitle });
  }, [setOptions, headerTitle]);

  // Dynamic iFrame URL based on received navigation parameter.
  const oneTrustUrl = isAccessRequest ? ONE_TRUST_ACCESS_URL : ONE_TRUST_DELETE_URL;

  // This is just in case, it is not possible to navigate here directly.
  if (Platform.OS !== 'web') return null; // disabled on native platforms.

  return (
    <TransparentModal
      accessibilityLabel={headerTitle}
      size={match(['full', 'large'])}
      onClose={handleDismiss}
    >
      {currentBreakpoint.isXS ? null : (
        <Modal.Header onClose={handleDismiss}>{headerTitle}</Modal.Header>
      )}
      <iframe
        className="optanon-category-C0001" // OneTrust auto-block exemption
        data-ot-ignore // OneTrust auto-block exemption
        src={oneTrustUrl}
        style={match([frameStyleSM, frameStyleLG])}
        id="one-trust-ca"
        title="one-trust-ca"
      />
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessCta: {
    defaultMessage: 'Request to access your data',
    description: 'Personal Data | Access CTA',
  },
  deleteCta: {
    defaultMessage: 'Delete your data & account',
    description: 'Personal Data | Delete CTA',
  },
});

// ─── Constants ──────────────────────────────────────────────────────
const ONE_TRUST_ACCESS_URL =
  'https://privacyportal.onetrust.com/webform/21a1d4f5-76a6-4640-87ad-0e9b616c1963/61a7235b-359e-4316-8c79-03620685bd69';
const ONE_TRUST_DELETE_URL =
  'https://privacyportal.onetrust.com/webform/21a1d4f5-76a6-4640-87ad-0e9b616c1963/b9ce941b-7bf5-4377-8ef0-ddb32fa17a89';

// ─── Styles ─────────────────────────────────────────────────────────
const frameStyleSM = { border: 0, height: '100%' };
const frameStyleLG = { border: 0, height: 460, maxHeight: '100%' };
